import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Class, GradeLevel, Teacher } from '@/interface/types'

const classSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  gradeId: z.string().min(1, 'Grade is required'),
  supervisorId: z.string().min(1, 'Supervisor is required'),
})

type ClassFormValues = z.infer<typeof classSchema>
export type { ClassFormValues }

interface ClassFormProps {
  initialData?: Partial<Class>
  grades: GradeLevel[]
  teachers: Teacher[]
  onSubmit: (data: ClassFormValues) => Promise<void>
  onCancel: () => void
  submitLabel: string
}

/**
 * A reusable form component for creating and editing classes
 * @param {ClassFormProps} props - The props for the ClassForm component
 * @returns {JSX.Element} The rendered ClassForm component
 */
export function ClassForm({
  initialData,
  grades,
  teachers,
  onSubmit,
  onCancel,
  submitLabel,
}: ClassFormProps) {
  const { t } = useTranslation('admin', { keyPrefix: 'etablissment-folder' })
  const form = useForm({
    defaultValues: {
      name: initialData?.name || '',
      gradeId: initialData?.gradeId || '',
      supervisorId: initialData?.supervisorId || '',
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value as ClassFormValues)
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-1 gap-4">
        <form.Field
          name="name"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('classForm.fields.name.validation.required')
              if (value.length < 2) return t('classForm.fields.name.validation.minLength')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="name">{t('classForm.fields.name.label')}</Label>
              <Input
                id="name"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('classForm.fields.name.placeholder')}
              />
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="gradeId"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('classForm.fields.gradeId.validation.required')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="gradeId">{t('classForm.fields.gradeId.label')}</Label>
              <Select
                value={field.state.value}
                onValueChange={field.handleChange}
              >
                <SelectTrigger id="gradeId">
                  <SelectValue placeholder={t('classForm.fields.gradeId.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {grades.map((grade) => (
                    <SelectItem key={grade.id} value={String(grade.id)}>
                      {grade.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>

        <form.Field
          name="supervisorId"
          validators={{
            onChange: ({ value }) => {
              if (!value) return t('classForm.fields.supervisorId.validation.required')
              return undefined
            },
          }}
        >
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor="supervisorId">{t('classForm.fields.supervisorId.label')}</Label>
              <Select
                value={field.state.value}
                onValueChange={field.handleChange}
              >
                <SelectTrigger id="supervisorId">
                  <SelectValue placeholder={t('classForm.fields.supervisorId.placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map((teacher) => (
                    <SelectItem key={teacher.id} value={String(teacher.id)}>
                      {teacher.firstname} {teacher.lastname}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {field.state.meta.errors && (
                <p className="text-sm text-red-500">
                  {field.state.meta.errors.join(', ')}
                </p>
              )}
            </div>
          )}
        </form.Field>
      </div>

      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('classForm.buttons.cancel')}
        </Button>
        <Button type="submit">{submitLabel}</Button>
      </div>
    </form>
  )
}
